import { useCallback, useRef, useState } from 'react'

import { Checkbox, ActionIcon, Loader } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { SelectState } from 'src/components/Combobox/SelectState/SelectState'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconTrash from 'src/icons/IconTrash'
import { buildAddressUpdates, formatAddress } from 'src/lib/address.utils'
import { standardizeCountry } from 'src/lib/country.utils'
import {
  isCompletePostalCode,
  lookupZipCode,
  normalizePostalCode,
} from 'src/lib/zipLookup'
import { stateAbbreviations } from 'src/utils/address/states'
import { isValidMonthRange } from 'src/utils/date/toMonthYearKey'

import { getUtcMonth } from '../utils'

import PlacesAutocomplete from './AddressInput/PlacesAutocomplete'
import ApplicationCard from './ApplicationCard/ApplicationCard'
import MonthInput from './MonthInput/MonthInput'
import TextInput from './TextInput/TextInput'

// "MM/YYYY – MM/YYYY" range, monospace-rendered on the timeline row. A null
// end date reads as "Current" — the applicant still lives there.
const formatRange = (startDate?: Date, endDate?: Date | null) =>
  `${getUtcMonth(startDate, '—')} – ${getUtcMonth(endDate, 'Current')}`

const AddressInformationCard = ({ values = [], handleUpdateApplication }) => {
  const [editing, setEditing] = useState(false)
  // Read view: which timeline nodes are expanded (keyed by id/index).
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Edit view: which address is open in the focused panel.
  const [activeIndex, setActiveIndex] = useState(0)

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Recruiters capture addresses over the phone, so everything is optional
  // except a way to locate the address: at least a city or a zip code. The
  // driver fills in the exact details later through the application flow,
  // which keeps its own strict validation.
  const schema = z.object({
    addresses: z.array(
      z
        .object({
          addressId: z.string().optional().nullable(),
          city: z
            .string()
            .max(100, { message: 'The name of your city is too long' }),
          state: z
            .string()
            .trim()
            .transform((val) => val.toUpperCase())
            .refine(
              (value) => value === '' || stateAbbreviations.includes(value),
              {
                message: 'Invalid state code',
              }
            ),
          zipCode: z
            .string()
            .trim()
            .refine(
              (value) =>
                value === '' || (value.length >= 5 && value.length <= 100),
              { message: 'Invalid postal code, try being more specific' }
            ),
          street: z
            .string()
            .max(500, { message: 'Your street name is too long' })
            .optional(),
          country: z
            .string()
            .max(100, { message: 'The name of your country is too long' }),
          startDate: z
            .date({ invalid_type_error: 'This is not a valid date' })
            .refine((data) => data < new Date(), {
              message: 'You must select a date in the past',
            })
            .nullish(),
          endDate: z
            .date({ invalid_type_error: 'This is not a valid date' })
            .nullish(),
        })
        .superRefine((address, ctx) => {
          if (!address.city?.trim() && !address.zipCode?.trim()) {
            for (const path of ['city', 'zipCode']) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [path],
                message: 'Enter at least a city or postal code',
              })
            }
          }
        })
    ),
  })

  // Quick phone entry captures where the driver lives now, so a new address
  // starts as the current one (no end date).
  const defaultAddress = {
    addressId: undefined,
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    startDate: undefined,
    endDate: null,
  }

  // Reverse-chronological (newest → oldest), matching the Accidents card: the
  // current address (no end date) first, then by end date, newest start date
  // breaking ties. Undated rows (incomplete data) sort to the bottom. Applied
  // at form initialization, cancel, AND after save so that adding or re-dating
  // an address can't leave the timeline out of order.
  const sortReverseChronological = (addresses) =>
    [...addresses].sort((a, b) => {
      // Current addresses (no end date) always first.
      const aEnd = a.endDate ? new Date(a.endDate).getTime() : Infinity
      const bEnd = b.endDate ? new Date(b.endDate).getTime() : Infinity
      if (aEnd !== bEnd) return bEnd - aEnd
      const aStart = a.startDate ? new Date(a.startDate).getTime() : -Infinity
      const bStart = b.startDate ? new Date(b.startDate).getTime() : -Infinity
      return bStart - aStart
    })

  // Coalesce nulls to '' — partially entered addresses come back from the API
  // with null fields, and the string inputs expect strings.
  const mapToFormValues = (list) =>
    sortReverseChronological(
      list.map((address) => ({
        ...(address?.addressId && { addressId: address.addressId }),
        city: address.city ?? '',
        state: address.state ?? '',
        zipCode: address.zipCode ?? '',
        street: address.street ?? '',
        country: address.country ?? '',
        startDate: address?.startDate
          ? new Date(address.startDate)
          : address.startDate,
        endDate: address?.endDate ? new Date(address.endDate) : address.endDate,
      }))
    )

  const form = useForm({
    initialValues: {
      addresses: mapToFormValues(values),
    },
    validate: zodResolver(schema),
  })

  // Wraps setEditing so entering edit focuses the first address.
  const handleSetEditing = (val: boolean) => {
    if (val) setActiveIndex(0)
    setEditing(val)
  }

  const handleCancel = () => {
    form.setValues({ addresses: mapToFormValues(values) })
    setEditing(false)
  }

  const handleAdd = () => {
    const newIndex = form.values.addresses.length
    setEditing(true)
    form.insertListItem(`addresses`, defaultAddress)
    setActiveIndex(newIndex)
  }

  const handleDelete = (index) => {
    form.removeListItem(`addresses`, index)
    setActiveIndex((current) => {
      if (index === current) return Math.max(0, index - 1)
      if (index < current) return current - 1
      return current
    })
  }

  // Zip-first phone entry: once the postal code is complete (US/MX 5-digit
  // or Canadian A1A 1A1), look up city/state/country and fill whatever the
  // recruiter hasn't typed yet. The token ignores responses that arrive
  // after the zip changed again, and the ref carries the latest values into
  // the async callback (the closure's form.values would be stale by the
  // time the lookup resolves).
  const [isZipLookupPending, setIsZipLookupPending] = useState(false)
  const zipLookupToken = useRef(0)
  const latestAddressesRef = useRef(form.values.addresses)
  latestAddressesRef.current = form.values.addresses

  const handleZipCodeChange = (index: number, rawValue: string) => {
    form.setFieldValue(`addresses.${index}.zipCode`, rawValue)

    const zip = rawValue.trim()
    if (!isCompletePostalCode(zip)) return

    const token = ++zipLookupToken.current
    setIsZipLookupPending(true)
    lookupZipCode(zip)
      .then((result) => {
        if (!result || token !== zipLookupToken.current) return
        const current = latestAddressesRef.current[index]
        // The row may have been deleted or re-typed while the lookup ran.
        // Compare stripped forms so a space added mid-flight (T2P1J9 →
        // T2P 1J9) or a ZIP+4 suffix still counts as the same code.
        if (
          !current ||
          !normalizePostalCode(current.zipCode).startsWith(
            normalizePostalCode(zip)
          )
        ) {
          return
        }

        // Fill only empty fields — a lookup must never overwrite manual entry.
        if (!current.city && result.city) {
          form.setFieldValue(`addresses.${index}.city`, result.city)
        }
        if (
          !current.state &&
          stateAbbreviations.includes(result.state.toUpperCase())
        ) {
          form.setFieldValue(
            `addresses.${index}.state`,
            result.state.toUpperCase()
          )
        }
        if (!current.country && result.country) {
          form.setFieldValue(
            `addresses.${index}.country`,
            standardizeCountry(result.country)
          )
        }
      })
      .catch(() => {
        // The lookup is a convenience — a failure just means no auto-fill.
      })
      .finally(() => {
        if (token === zipLookupToken.current) setIsZipLookupPending(false)
      })
  }

  const handleSave = useCallback(() => {
    const updates = {
      addresses: sortReverseChronological(form.values.addresses),
    }

    handleUpdateApplication(updates).then((res) => {
      if (res && !res.errors) {
        setEditing(false)
        form.setValues({
          addresses: mapToFormValues(res.data.updateApplication.addresses),
        })
      }
    })
  }, [form.values.addresses, handleUpdateApplication])

  const addresses = form.values.addresses
  // Keep the focused index in range as addresses are added/removed.
  const activeIdx = Math.min(activeIndex, addresses.length - 1)

  return (
    <ApplicationCard
      title={'Addresses'}
      editing={editing}
      isEmpty={addresses.length === 0}
      setEditing={handleSetEditing}
      isSaveDisabled={!form.isValid()}
      handleSave={handleSave}
      handleCancel={handleCancel}
      handleAdd={handleAdd}
    >
      {/* Timeline: a vertical spine threads each address node, matching the
          Accidents & Violations card. */}
      <div className="relative pl-6">
        <div className="absolute bottom-2 left-[5px] top-2 w-px bg-doubleNickel-gray-200" />

        {addresses.map((value, index) => {
          const key = value.addressId || `idx-${index}`
          const isOpen = expanded.has(key)
          const label = formatAddress(value)

          // ---- Edit mode ----
          if (editing) {
            // Non-focused addresses stay as dimmed read rows; click to focus.
            if (index !== activeIdx) {
              return (
                <div key={key} className="relative pb-[18px] last:pb-0">
                  <span className="absolute -left-[23px] top-1.5 z-[1] h-[11px] w-[11px] rounded-full border-2 border-doubleNickel-gray-300 bg-doubleNickel-white" />
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="flex w-full items-center gap-2 rounded px-1.5 py-2 text-left opacity-60 hover:bg-doubleNickel-gray-50 hover:opacity-100"
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-doubleNickel-gray-900"
                      style={{ fontSize: 13, fontWeight: 600 }}
                      title={label || 'New address'}
                    >
                      {label || 'New address'}
                    </span>
                    <span
                      className="ml-auto whitespace-nowrap text-xs text-doubleNickel-gray-700"
                      style={{ fontFamily: 'monospace' }}
                    >
                      {formatRange(value.startDate, value.endDate)}
                    </span>
                  </button>
                </div>
              )
            }

            // Focused address: a white panel with the inline inputs.
            return (
              <div key={key} className="relative pb-[18px] last:pb-0">
                <span className="absolute -left-[23px] top-1.5 z-[1] h-[11px] w-[11px] rounded-full border-2 border-doubleNickel-gray-700 bg-doubleNickel-gray-700" />
                <div className="rounded-lg border border-doubleNickel-gray-300 bg-doubleNickel-white p-3.5 shadow-sm">
                  <div className="mb-3 text-xs text-doubleNickel-gray-500">
                    Quick entry: a postal code fills in the city and state, and
                    only a city or postal code is needed to save — the driver
                    confirms the exact address later.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <PlacesAutocomplete
                      label={'Street Address'}
                      displayStreetOnly
                      value={form.values.addresses[index]}
                      handleChange={(value) => {
                        form.setFieldValue(`addresses.${index}`, {
                          ...form.values.addresses[index],
                          ...buildAddressUpdates(value),
                        })
                      }}
                    />
                    <TextInput
                      label="Postal Code"
                      editing
                      value={form.values.addresses[index].zipCode}
                      rightSection={
                        isZipLookupPending && <Loader size={12} color="gray" />
                      }
                      handleChange={(value) =>
                        handleZipCodeChange(index, value)
                      }
                    />
                    <TextInput
                      label="City"
                      editing
                      value={form.values.addresses[index].city}
                      handleChange={(value) =>
                        form.setFieldValue(`addresses.${index}.city`, value)
                      }
                    />
                    <SelectState
                      label="State"
                      value={form.values.addresses[index].state}
                      setValue={(value) =>
                        form.setFieldValue(`addresses.${index}.state`, value)
                      }
                    />
                    <TextInput
                      label="Country"
                      editing
                      value={form.values.addresses[index].country}
                      handleChange={(value) =>
                        form.setFieldValue(`addresses.${index}.country`, value)
                      }
                      onBlur={() => {
                        // Standardize country names when user finishes editing
                        const value = form.values.addresses[index].country
                        if (!value) return

                        const standardized = standardizeCountry(value)
                        if (standardized !== value) {
                          form.setFieldValue(
                            `addresses.${index}.country`,
                            standardized
                          )
                        }
                      }}
                    />
                    <MonthInput
                      label="Start Date"
                      editing={true}
                      value={value.startDate}
                      _maxDate={
                        value.endDate
                          ? new Date(value.endDate).toISOString()
                          : new Date().toISOString()
                      }
                      handleChange={(date) => {
                        // Validate BEFORE updating the form value
                        if (isValidMonthRange(date, value.endDate)) {
                          form.setFieldValue(
                            `addresses.${index}.startDate`,
                            date
                          )
                        }
                      }}
                    />
                    <div className="flex flex-col gap-2">
                      <MonthInput
                        label="End Date"
                        editing={true}
                        disabled={
                          value.endDate === null || value.endDate === undefined
                        }
                        value={value.endDate}
                        _maxDate={new Date().toISOString()}
                        _minDate={
                          value.startDate
                            ? new Date(value.startDate).toISOString()
                            : undefined
                        }
                        handleChange={(date) => {
                          // Validate BEFORE updating the form value
                          if (isValidMonthRange(value.startDate, date)) {
                            form.setFieldValue(
                              `addresses.${index}.endDate`,
                              date
                            )
                          }
                        }}
                      />
                      <Checkbox
                        label="Current address"
                        size="xs"
                        checked={
                          value.endDate === undefined || value.endDate === null
                        }
                        onClick={() => {
                          form.setFieldValue(
                            `addresses.${index}.endDate`,
                            value.endDate === null ||
                              value.endDate === undefined
                              ? new Date()
                              : null
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-3.5 flex justify-end border-t border-doubleNickel-gray-200 pt-3">
                    <ActionIcon
                      className="h-8 w-8 border border-doubleNickel-gray-200 bg-doubleNickel-white shadow-sm hover:bg-doubleNickel-error-25"
                      onClick={() => handleDelete(index)}
                    >
                      <IconTrash className="h-4 w-4 fill-none stroke-doubleNickel-error-500" />
                    </ActionIcon>
                  </div>
                </div>
              </div>
            )
          }

          // ---- Read mode: collapsed summary, expands to detail ----
          return (
            <div key={key} className="relative pb-[18px] last:pb-0">
              <span className="absolute -left-[23px] top-1.5 z-[1] h-[11px] w-[11px] rounded-full border-2 border-doubleNickel-gray-300 bg-doubleNickel-white" />
              <button
                type="button"
                onClick={() => toggleExpanded(key)}
                className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-doubleNickel-gray-50"
                aria-expanded={isOpen}
              >
                <span
                  className="min-w-0 flex-1 truncate text-doubleNickel-gray-900"
                  style={{ fontSize: 13, fontWeight: 600 }}
                  title={label || 'Unknown address'}
                >
                  {label || 'Unknown address'}
                </span>
                <span
                  className="ml-auto whitespace-nowrap text-xs text-doubleNickel-gray-700"
                  style={{ fontFamily: 'monospace' }}
                >
                  {formatRange(value.startDate, value.endDate)}
                </span>
                <IconChevronDown
                  className={`h-3.5 w-3.5 flex-none fill-none stroke-doubleNickel-gray-400 transition-transform ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isOpen && (
                <div className="flex flex-wrap gap-7 px-1.5 pb-1 pt-2">
                  {[
                    { label: 'Street', value: value.street },
                    { label: 'City', value: value.city },
                    { label: 'State', value: value.state },
                    { label: 'Postal Code', value: value.zipCode },
                    { label: 'Country', value: value.country },
                  ].map((f) => (
                    <div key={f.label}>
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-doubleNickel-gray-400">
                        {f.label}
                      </div>
                      <div className="text-xs text-doubleNickel-gray-800">
                        {f.value || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </ApplicationCard>
  )
}

export default AddressInformationCard
